import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import React, { useMemo, useRef } from "react";
import PropTypes from "prop-types";

/**
 * Отображаемый статус чека и его цвет.
 *
 * Внутренних статусов у чека больше, чем нужно оператору: приём, начисление и фискализация —
 * стадии одного ожидания, действий по ним всё равно не предпринять. Поэтому всё промежуточное
 * сводится к «В обработке», а различать стадии можно по журналу в ptc-fiscal-service.
 */
const STATUS_VIEW = {
    SUCCESS: { label: "Успешно", severity: "success" },
    FAILED: { label: "Ошибка", severity: "danger" },
    SKIPPED: { label: "Не подлежит фискализации", severity: "warning" },
    NEW: { label: "В обработке", severity: "info" },
    ACCRUAL_PENDING: { label: "В обработке", severity: "info" },
    FISCAL_PENDING: { label: "В обработке", severity: "info" },
    IN_PROGRESS: { label: "В обработке", severity: "info" },
};

/**
 * Состояние отката. Отдельно от статуса чека: отозванный чек остаётся успешно фискализированным —
 * отказ от него это новый факт, а не отмена прежнего, и оператор должен видеть оба.
 */
const ROLLBACK_VIEW = {
    ROLLBACK_PENDING: { label: "Откат в работе", severity: "info" },
    ROLLED_BACK: { label: "Отозван", severity: "warning" },
    ROLLBACK_FAILED: { label: "Откат не выполнен", severity: "danger" },
};

/**
 * Сумма для отчёта: разряды пробелами, две цифры после запятой.
 * Пустое значение остаётся прочерком, а не нулём — ноль читался бы как «отчислений нет»,
 * тогда как суммы у чека попросту не было.
 */
const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const number = Number(value);
    if (Number.isNaN(number)) return String(value);
    return number.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDateTime = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AvrCheckTable = ({ page, pageSize, onPageChange, data, totalItems, canRollback, onRollback, rollbackingAvrId }) => {
    const table = useRef();

    const normalizedData = useMemo(() => {
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.content)) return data.content;
        return [];
    }, [data]);

    const totalRecords = useMemo(() => {
        if (typeof totalItems === "number") return totalItems;
        if (data && typeof data.totalElements === "number") return data.totalElements;
        return 0;
    }, [data, totalItems]);

    // Сквозная нумерация по всем страницам, а не с единицы на каждой:
    // оператор называет номер строки, и он должен быть однозначным.
    const rowNumberTemplate = (_row, options) => page * pageSize + options.rowIndex + 1;

    const statusTemplate = (row) => {
        const view = STATUS_VIEW[row?.status];
        if (!view) return row?.status || "";
        return <Tag value={view.label} severity={view.severity} />;
    };

    /**
     * Причина ошибки в виде «КОД: текст». Код приходит отдельным полем: по нему группируют
     * однотипные отказы, тогда как текст ProsperPay может отличаться от случая к случаю.
     * Если кода нет — показываем только текст, а не «null: текст».
     */
    const errorTemplate = (row) => {
        const code = row?.errorCode;
        const message = row?.errorMessage;
        if (!code && !message) return "—";
        if (!code) return message;
        if (!message) return code;
        return `${code}: ${message}`;
    };

    const dateTemplate = (field) => (row) => formatDateTime(row?.[field]);

    const amountTemplate = (field) => (row) => formatAmount(row?.[field]);

    const rollbackStatusTemplate = (row) => {
        const view = ROLLBACK_VIEW[row?.rollbackStatus];
        if (!view) return "—";
        return <Tag value={view.label} severity={view.severity} />;
    };

    /**
     * Кнопка показывается только тем, у кого есть право, и только для чеков, которые сейчас
     * действительно можно отозвать. Признак считает ptc-fiscal-service: правило включает окно
     * отката в 14 дней от отправки, и повторять его здесь значило бы показывать кнопку,
     * отвечающую отказом, — или прятать ту, что сработала бы.
     */
    const rollbackTemplate = (row) => {
        if (!canRollback) return null;
        if (!row?.rollbackAvailable) {
            return row?.rollbackStatus ? null : (
                <span title={rollbackHint(row)} className="p-text-secondary">—</span>
            );
        }
        return (
            <Button
                label="Откатить"
                icon="pi pi-undo"
                className="p-button-sm p-button-danger p-button-outlined"
                loading={rollbackingAvrId === row.avrId}
                disabled={rollbackingAvrId != null}
                onClick={() => onRollback(row)}
            />
        );
    };

    /** Почему кнопки нет: у оператора не должно оставаться вопросов к пустой ячейке. */
    const rollbackHint = (row) => {
        if (row?.status !== "SUCCESS") return "Отозвать можно только фискализированный чек";
        if (row?.rollbackDeadline) return `Окно отката закрылось ${formatDateTime(row.rollbackDeadline)}`;
        return "Откат недоступен";
    };

    return (
        <div className="card">
            <DataTable
                ref={table}
                value={normalizedData}
                className="p-datatable-customers"
                dataKey="avrId"
                rowHover
                emptyMessage="Данные не найдены"
                scrollable
            >
                <Column headerStyle={{ width: "60px" }} header="№" body={rowNumberTemplate} />
                <Column headerStyle={{ width: "180px" }} field="slqNum" header="Номер SLQ" sortable />
                <Column headerStyle={{ width: "180px" }} field="checkNumber" header="ID чека" sortable />
                <Column headerStyle={{ width: "280px" }} field="fio" header="ФИО" sortable />
                <Column headerStyle={{ width: "160px" }} field="iin" header="ИИН" sortable />
                <Column
                    headerStyle={{ width: "220px" }}
                    field="signedAt"
                    header="Дата и время подписания АВР"
                    body={dateTemplate("signedAt")}
                    sortable
                />
                <Column
                    headerStyle={{ width: "190px" }}
                    field="receiptAmount"
                    header="Сумма чека (гросс)"
                    body={amountTemplate("receiptAmount")}
                    bodyStyle={{ textAlign: "right" }}
                    sortable
                />
                <Column
                    headerStyle={{ width: "170px" }}
                    field="kbk901101Opv"
                    header="КБК 901101 (ОПВ)"
                    body={amountTemplate("kbk901101Opv")}
                    bodyStyle={{ textAlign: "right" }}
                />
                <Column
                    headerStyle={{ width: "170px" }}
                    field="kbk902101So"
                    header="КБК 902101 (СО)"
                    body={amountTemplate("kbk902101So")}
                    bodyStyle={{ textAlign: "right" }}
                />
                <Column
                    headerStyle={{ width: "180px" }}
                    field="kbk904101Osms"
                    header="КБК 904101 (ОСМС)"
                    body={amountTemplate("kbk904101Osms")}
                    bodyStyle={{ textAlign: "right" }}
                />
                <Column
                    headerStyle={{ width: "180px" }}
                    field="kbk905101Opvr"
                    header="КБК 905101 (ОПВР)"
                    body={amountTemplate("kbk905101Opvr")}
                    bodyStyle={{ textAlign: "right" }}
                />
                <Column
                    headerStyle={{ width: "160px" }}
                    field="status"
                    header="Статус чека"
                    body={statusTemplate}
                    sortable
                />
                <Column headerStyle={{ width: "360px" }} header="Причина ошибки" body={errorTemplate} />
                <Column headerStyle={{ width: "170px" }} header="Откат" body={rollbackStatusTemplate} />
                {canRollback && (
                    <Column headerStyle={{ width: "150px" }} header="" body={rollbackTemplate} />
                )}
            </DataTable>
            <Paginator
                currentPageReportTemplate="Показано с {first} по {last} из {totalRecords} записей"
                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                first={page * pageSize}
                rows={pageSize}
                totalRecords={totalRecords}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onPageChange={onPageChange}
            />
        </div>
    );
};

AvrCheckTable.propTypes = {
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    data: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.shape({
            content: PropTypes.array,
            totalElements: PropTypes.number,
        }),
    ]).isRequired,
    totalItems: PropTypes.number,
    /** Есть ли у текущего пользователя право PAY_ADMIN. */
    canRollback: PropTypes.bool,
    onRollback: PropTypes.func,
    /** АВР, по которому откат сейчас выполняется: блокирует повторные нажатия. */
    rollbackingAvrId: PropTypes.number,
};

AvrCheckTable.defaultProps = {
    canRollback: false,
    onRollback: () => {},
    rollbackingAvrId: null,
};

export default AvrCheckTable;
